// Ring 0 — pure level data, NO Phaser imports.
//
// Slice floor for Phase 1 Pre-flop: a 640×240 single-room floor (2× the
// native 320 wide screen) with two divergent paths that prove the
// suit-asymmetric pickup mechanic (D-04, D-05, D-06) before any pickup
// logic exists. Wave 4 wires CardPickup entities; Wave 1B (this plan)
// only consumes the geometry and the player spawn.
//
// HAND-CODED, not Tiled (RESEARCH Open-Q #1 resolution lines 1115–1172).
// Tiled tax not justified for one floor; the FloorRect/FloorSpawn shape
// maps cleanly into Tiled tile-layers if Phase 3 wants the visual editor
// later.
//
// Path layout:
//   - Floor strip at y=224 (full width).
//   - HIGH path: ♥-biased platforms at y ∈ {64, 80, 96}.
//   - LOW path:  ♠-biased platforms at y ∈ {120, 144, 168}.
//   - Convergence: two platforms near x ≈ 460–520 so both paths meet
//     before the right wall (Aces of each suit live there).
//   - RANK TRAP: a King-of-spades on the low path's deepest platform.
//     A player going for a low-suited straight either detours to grab it
//     and contaminates the suited goal, or skips it and surrenders the
//     rank — the moat (D-04 / D-05 / D-06).

import type { Card } from '../../run/types';

export interface FloorRect {
  x: number;
  y: number;
  w: number;
  h: number;
  oneWay?: boolean;
}

export interface FloorSpawn {
  type: 'player' | 'card';
  x: number;
  y: number;
  card?: Card;
}

export interface FloorConfig {
  key: 'pre-flop';
  bgColor: number;
  geometry: FloorRect[];
  spawns: FloorSpawn[];
  width: number;
  height: number;
}

export const FLOOR_PRE_FLOP: FloorConfig = {
  key: 'pre-flop',
  bgColor: 0x0a0a14,
  width: 640,
  height: 240,
  geometry: [
    // Solid floor (full width)
    { x: 0, y: 224, w: 640, h: 16 },

    // LOW path (♠-biased) — three one-way platforms ascending right
    { x: 100, y: 168, w: 80, h: 8, oneWay: true }, // low platform 1
    { x: 220, y: 144, w: 80, h: 8, oneWay: true }, // low platform 2
    { x: 340, y: 120, w: 80, h: 8, oneWay: true }, // low platform 3 (rank-trap host)

    // HIGH path (♥-biased) — three one-way platforms ascending right
    { x: 60, y: 96, w: 80, h: 8, oneWay: true }, // high platform 1
    { x: 180, y: 64, w: 80, h: 8, oneWay: true }, // high platform 2 (a touch lower than line so jumps clear)
    { x: 300, y: 48, w: 80, h: 8, oneWay: true }, // high platform 3

    // Convergence platforms — both paths meet here
    { x: 460, y: 80, w: 60, h: 8, oneWay: true }, // converging high (♥A target)
    { x: 460, y: 160, w: 60, h: 8, oneWay: true }, // converging low (♠A target)
  ],
  spawns: [
    // Player spawn on the floor, hard-left
    { type: 'player', x: 16, y: 200 },

    // ♥-biased high path (D-05) — three hearts; T♥ is the easy high-path payoff
    { type: 'card', x: 100, y: 80, card: { rank: '5', suit: 'h' } },
    { type: 'card', x: 220, y: 48, card: { rank: '7', suit: 'h' } },
    { type: 'card', x: 340, y: 32, card: { rank: 'T', suit: 'h' } },

    // ♠-biased low path (D-05) — two suited cards plus the rank trap
    { type: 'card', x: 140, y: 152, card: { rank: '5', suit: 's' } },
    { type: 'card', x: 260, y: 128, card: { rank: '7', suit: 's' } },
    // RANK TRAP — King-of-spades on low platform 3. A player chasing a
    // low straight will detour for it (contaminating the suit-flush
    // goal) or skip it (surrendering the rank). D-04 / D-05 / D-06.
    { type: 'card', x: 380, y: 104, card: { rank: 'K', suit: 's' } },

    // Convergent zone — both paths can reach an Ace
    { type: 'card', x: 480, y: 64, card: { rank: 'A', suit: 'h' } },
    { type: 'card', x: 480, y: 144, card: { rank: 'A', suit: 's' } },
  ],
};
